import { useContext, useMemo } from 'react';
import MetricOptionsContext from '../contexts/MetricOptionsContext';
import isDefined from '../isDefined';
import metricTypeCheckers from '../types/metricTypeCheckers';
import _ from 'lodash';

export interface UsedMetrics {
  directlyUsed: Metrics.Metric[];
  inputsForCompounds: Metrics.NormalMetric[];
  usedMetrics: Metrics.Metric[];
}

export const getUsedMetrics = ({
  series,
  metricOptionsLookup,
}: {
  series: V5ChartDefinitionSeries[];
  metricOptionsLookup: { [metricId: string]: Metrics.Metric | undefined };
}): UsedMetrics => {
  const directlyUsed = _.uniqBy(
    series
      .map((s) => s.metricId)
      .map((mId) => metricOptionsLookup[mId])
      .filter(isDefined),
    'id',
  );
  const inputsForCompounds = _.uniqBy(
    directlyUsed
      .filter(metricTypeCheckers.isCompoundMetric)
      .reduce((acc, cm) => {
        const inputs = cm.metricIds
          .map((mid) => metricOptionsLookup[mid])
          .filter(isDefined)
          .filter(metricTypeCheckers.isNormalMetric);
        return [...acc, ...inputs];
      }, [] as Metrics.NormalMetric[]),
    'id',
  );

  return {
    directlyUsed,
    inputsForCompounds,
    usedMetrics: [...directlyUsed, ...inputsForCompounds],
  };
};

const useUsedMetrics = (chartDef: V5ChartDefinition) => {
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const usedMetrics = useMemo(
    (): UsedMetrics =>
      getUsedMetrics({ series: chartDef.series, metricOptionsLookup }),
    [chartDef.series, metricOptionsLookup],
  );

  return usedMetrics;
};

export default useUsedMetrics;
